import React, { useEffect, useState } from "react";
import {
  Alert,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import * as SC from "SC";
import { httpService } from "api/httpService";
import { useAppSelector } from "store/hooks";

const PLAN_OPTIONS = [
  { value: "starter", label: "Starter ($99/mo)" },
  { value: "professional", label: "Professional ($199/mo)" },
  { value: "enterprise", label: "Enterprise ($299/mo)" },
];

const STATUS_LABEL = {
  active: { label: "Active", color: "success" },
  trialing: { label: "Trial", color: "info" },
  past_due: { label: "Past Due", color: "warning" },
  canceled: { label: "Canceled", color: "error" },
  incomplete: { label: "Incomplete", color: "warning" },
  paused: { label: "Paused", color: "default" },
};

const formatRenewal = (timestampMs) => {
  if (!timestampMs) return "—";
  try {
    return new Date(timestampMs).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

export const BillingPage = () => {
  const branding = useAppSelector((state) => state.branding);

  const [summary, setSummary] = useState({
    plan: null,
    planStatus: null,
    currentPeriodEnd: null,
  });
  const [selectedPlan, setSelectedPlan] = useState("professional");
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [usageRes, settingsRes] = await Promise.all([
        httpService({ url: "/tenant/usage-summary", options: { method: "GET" } }),
        httpService({ url: "/tenant/settings", options: { method: "GET" } }),
      ]);
      if (cancelled) return;

      const plan = settingsRes.value?.plan || null;
      const planStatus = settingsRes.value?.status || null;
      const currentPeriodEnd = null;

      setSummary({ plan, planStatus, currentPeriodEnd });
      setUsage(usageRes.value || null);
      if (plan) setSelectedPlan(plan);
      if (usageRes.error && settingsRes.error) {
        setError(usageRes.error || settingsRes.error);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openPortal = async () => {
    setActionLoading(true);
    setError(null);
    const { value, error: err } = await httpService({
      url: "/billing/portal-session",
      options: {
        method: "POST",
        data: { returnUrl: `${window.location.origin}/billing` },
      },
    });
    setActionLoading(false);
    if (err) {
      setError(err);
      return;
    }
    if (value?.url) {
      window.location.href = value.url;
    } else {
      setError("No portal URL returned from server");
    }
  };

  const upgradePlan = async () => {
    setActionLoading(true);
    setError(null);
    const { value, error: err } = await httpService({
      url: "/billing/checkout-session",
      options: {
        method: "POST",
        data: {
          plan: selectedPlan,
          successUrl: `${window.location.origin}/billing/success`,
          cancelUrl: `${window.location.origin}/billing/cancel`,
        },
      },
    });
    setActionLoading(false);
    if (err) {
      setError(err);
      return;
    }
    if (value?.url) {
      window.location.href = value.url;
    } else {
      setError("No checkout URL returned from server");
    }
  };

  const statusMeta =
    STATUS_LABEL[summary.planStatus] ||
    { label: summary.planStatus || "—", color: "default" };

  return (
    <SC.OverflowedContainer>
      <Stack gap={3} sx={{ p: 3, maxWidth: 720 }}>
        <Stack>
          <Typography variant="h5" fontWeight={800}>
            Billing
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage {branding.companyName || "your"} subscription and payment method.
          </Typography>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            {loading ? (
              <Stack alignItems="center" sx={{ py: 3 }}>
                <CircularProgress size={24} />
              </Stack>
            ) : (
              <Stack gap={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Current plan
                  </Typography>
                  <Typography fontWeight={700} sx={{ textTransform: "capitalize" }}>
                    {summary.plan || "—"}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip label={statusMeta.label} color={statusMeta.color} size="small" />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Renews on
                  </Typography>
                  <Typography fontWeight={600}>
                    {formatRenewal(summary.currentPeriodEnd)}
                  </Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <SC.ControlButton
                  variant="contained"
                  onClick={openPortal}
                  disabled={actionLoading}
                >
                  {actionLoading ? <CircularProgress size={18} color="inherit" /> : "Manage Billing"}
                </SC.ControlButton>
                <Typography variant="caption" color="text.secondary">
                  Update payment method, download invoices, or cancel from Stripe's secure portal.
                </Typography>
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
              Current usage
            </Typography>
            {loading && !usage ? (
              <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
                <Skeleton variant="rounded" width={140} height={32} />
                <Skeleton variant="rounded" width={140} height={32} />
                <Skeleton variant="rounded" width={140} height={32} />
                <Skeleton variant="rounded" width={140} height={32} />
              </Stack>
            ) : (
            <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
              {(() => {
                const vehicles = usage?.vehicles ?? 0;
                const limit = usage?.vehicleLimit ?? 0;
                if (limit <= 0) {
                  return <Chip label={`Vehicles: ${vehicles} (unlimited)`} variant="outlined" />;
                }
                const pct = Math.min(100, Math.round((vehicles / limit) * 100));
                const color = vehicles >= limit ? "error" : pct >= 80 ? "warning" : "default";
                return (
                  <Chip
                    label={`Vehicles: ${vehicles} / ${limit}${pct >= 80 ? ` (${pct}%)` : ""}`}
                    color={color}
                    variant={color === "default" ? "outlined" : "filled"}
                  />
                );
              })()}
              {(() => {
                const drivers = usage?.drivers ?? 0;
                const limit = usage?.driverLimit ?? 0; // 0 = unlimited
                if (limit <= 0) {
                  return <Chip label={`Drivers: ${drivers} (unlimited)`} variant="outlined" />;
                }
                const pct = Math.min(100, Math.round((drivers / limit) * 100));
                const color = drivers >= limit ? "error" : pct >= 80 ? "warning" : "default";
                return (
                  <Chip
                    label={`Drivers: ${drivers} / ${limit}${pct >= 80 ? ` (${pct}%)` : ""}`}
                    color={color}
                    variant={color === "default" ? "outlined" : "filled"}
                  />
                );
              })()}
              {(() => {
                const rides = usage?.ridesThisMonth ?? 0;
                const quota = usage?.rideQuota ?? 0;
                if (quota <= 0) {
                  return <Chip label={`Rides this month: ${rides} (unlimited)`} variant="outlined" />;
                }
                const pct = Math.min(999, Math.round((rides / quota) * 100));
                const overage = Math.max(0, rides - quota);
                const color = overage > 0 ? "error" : pct >= 80 ? "warning" : "default";
                const label = overage > 0
                  ? `Rides: ${rides} / ${quota} (+${overage} overage)`
                  : `Rides: ${rides} / ${quota}${pct >= 80 ? ` (${pct}%)` : ""}`;
                return (
                  <Chip
                    label={label}
                    color={color}
                    variant={color === "default" ? "outlined" : "filled"}
                  />
                );
              })()}
              {(() => {
                const companies = usage?.customerCompanies ?? 0;
                const limit = usage?.customerCompanyLimit ?? 0;
                if (limit <= 0) {
                  return <Chip label={`Companies: ${companies} (unlimited)`} variant="outlined" />;
                }
                const pct = Math.min(100, Math.round((companies / limit) * 100));
                const color = companies >= limit ? "error" : pct >= 80 ? "warning" : "default";
                return (
                  <Chip
                    label={`Companies: ${companies} / ${limit}${pct >= 80 ? ` (${pct}%)` : ""}`}
                    color={color}
                    variant={color === "default" ? "outlined" : "filled"}
                  />
                );
              })()}
            </Stack>
            )}
            {(() => {
              const drivers = usage?.drivers ?? 0;
              const dLimit = usage?.driverLimit ?? 0;
              const vehicles = usage?.vehicles ?? 0;
              const vLimit = usage?.vehicleLimit ?? 0;
              const companies = usage?.customerCompanies ?? 0;
              const cLimit = usage?.customerCompanyLimit ?? 0;
              const atDriver = dLimit > 0 && drivers >= dLimit;
              const atVehicle = vLimit > 0 && vehicles >= vLimit;
              const atCompany = cLimit > 0 && companies >= cLimit;
              const nearDriver = dLimit > 0 && drivers / dLimit >= 0.8 && !atDriver;
              const nearVehicle = vLimit > 0 && vehicles / vLimit >= 0.8 && !atVehicle;
              const nearCompany = cLimit > 0 && companies / cLimit >= 0.8 && !atCompany;

              if (atDriver || atVehicle || atCompany) {
                const which = [atDriver && "driver", atVehicle && "vehicle", atCompany && "company"]
                  .filter(Boolean).join(" & ");
                return (
                  <Typography variant="caption" color="error" sx={{ display: "block", mt: 1.5, fontWeight: 600 }}>
                    You're at your {which} limit. New {which} entries will be rejected until you upgrade.
                  </Typography>
                );
              }
              if (nearDriver || nearVehicle || nearCompany) {
                const which = [nearDriver && "driver", nearVehicle && "vehicle", nearCompany && "company"]
                  .filter(Boolean).join(" & ");
                return (
                  <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 1.5 }}>
                    Approaching {which} limit — consider upgrading before you hit it.
                  </Typography>
                );
              }
              const rides = usage?.ridesThisMonth ?? 0;
              const rideQuota = usage?.rideQuota ?? 0;
              if (rideQuota > 0 && rides > rideQuota) {
                return (
                  <Typography variant="caption" color="error" sx={{ display: "block", mt: 1.5, fontWeight: 600 }}>
                    {rides - rideQuota} rides over your {rideQuota}-ride quota this month — these will appear as overage on your next invoice.
                  </Typography>
                );
              }
              return null;
            })()}
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
              Change plan
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} alignItems="stretch">
              <Select
                size="small"
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                sx={{ flex: 1 }}
              >
                {PLAN_OPTIONS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
              <SC.ControlButton
                variant="outlined"
                onClick={upgradePlan}
                disabled={actionLoading || selectedPlan === summary.plan}
              >
                {selectedPlan === summary.plan ? "Current plan" : "Upgrade Plan"}
              </SC.ControlButton>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Upgrading opens a new Stripe Checkout session to confirm the change.
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    </SC.OverflowedContainer>
  );
};

export default BillingPage;
